import { useState, useCallback, useRef, useEffect, useContext } from 'react';
import { toastService } from '../services/toastService';
import { generationService, GenerationOptions } from '../services/generationService';
import { authService } from '../services/authService';
import { apiClient } from '../services/apiClient';
import { useAuth } from './useAuth';
import { useLanguage } from '../contexts/LanguageContext';
import { useGenerationContext } from '../contexts/GenerationContext';
import { useQuota } from '../contexts/QuotaContext';
import { PostsContext } from '../contexts/PostsContext';
import type { Post } from '../types';
import {
  PostStatus, ChannelType, WS_INACTIVITY_TIMEOUT_MS, WS_GENERATION_HARDCAP_MS,
  GENERATION_LATE_POSTS_REFRESH_DELAY_MS,
} from '../utils/Constants';
import type { PlatformType, GenerationConfig } from '../types/generation';
import { Logger } from '../utils/logger';
import Config from '../config/config';

export type { GenerationConfig };

// WebSocket message types for generation progress/complete events
interface WsAgentMessage {
  i18n_key?: string;
  i18n_params?: Record<string, unknown>;
  role?: string;
  agent?: string;
  status?: string;
  content?: string;
  message?: string;
  timestamp?: string;
  details?: string;
  metadata?: { details?: string };
}

interface WsSourceReference {
  source_id?: string;
  id?: string;
  title?: string;
  url?: string;
  source_type?: string;
  type?: string;
  relevance_score?: number;
  relevance?: number;
  key_facts?: string[];
  facts?: string[];
  ai_summary?: string;
  post_angles?: string[];
  keywords?: string[];
  entity_names?: string[];
}

interface WsPost {
  id?: string;
  channel_id?: string;
  channelId?: string;
  space_id?: string;
  spaceId?: string;
  network?: string;
  platform?: string;
  content?: string;
  generated_date?: string;
  generatedDate?: string;
  created_at?: string;
  scheduled_date?: string;
  published_date?: string;
  source_inspiration?: string;
  generation_angle?: string;
  angle?: string;
  hashtags?: string[];
  status?: string;
  source_ids?: string[];
  source_urls?: string[];
  source_references?: WsSourceReference[];
  sources?: WsSourceReference[];
  compliance_scores?: unknown;
  knowledge_source_quality?: string;
  image_url?: string;
  preview?: string;
  media_items?: unknown[];
}

export interface GenerationSettings {
  useSources?: boolean;
  forceRefreshSources?: boolean;
  language?: string;
  // 2026-06-10 — orientation forcée de la génération (globale à tous les canaux).
  forcedTopic?: string; // Sujet/actualité imposé(e)
  forcedUrl?: string;   // Lien source à privilégier
}

export interface AgentProgress {
  agent: string;
  status: 'pending' | 'running' | 'completed' | 'error';
  message: string;
  timestamp: Date;
  details?: string;
  i18nKey?: string;
  i18nParams?: Record<string, unknown>;
}

export type PlatformStatus = 'pending' | 'generating' | 'completed' | 'error';

export interface PlatformProgress {
  platform: PlatformType;
  channelId: string;
  status: PlatformStatus;
  postsGenerated: number;
  postsRequested: number;
}

export interface GenerationProgressState {
  currentAgent: string;
  currentStep: string;
  progress: number; // 0-100
  messages: AgentProgress[];
  totalSteps: number;
  completedSteps: number;
  phase?: string;
  platforms: PlatformProgress[];
  currentPlatform?: PlatformType;
}

const API_URL = Config.getInstance().apiUrl;
const WS_URL = Config.getInstance().wsUrl;

/**
 * V4-UX-ERROR-RECOVER — Détecte si une erreur correspond à un timeout côté client
 * ou à un 504/502 côté serveur. Permet d'afficher une UX "réessayer" plutôt qu'un
 * message d'erreur générique.
 */
export function isTimeoutLikeError(err: unknown): boolean {
  if (!err) return false;
  // Axios: error.code === 'ECONNABORTED' ou status 504/502
  const anyErr = err as { code?: string; response?: { status?: number }; message?: string; name?: string };
  if (anyErr.code === 'ECONNABORTED') return true;
  if (anyErr.name === 'AbortError') return true;
  const status = anyErr.response?.status;
  if (status === 504 || status === 502 || status === 408) return true;
  const msg = String(anyErr.message ?? '').toLowerCase();
  return msg.includes('timeout')
    || msg.includes('timed out')
    || msg.includes('econnaborted')
    || msg.includes('network error');
}

/**
 * Translate an i18n key with parameters.
 * Converts backend keys like "ws.generation.started" to nested object access.
 */
function translateI18nKey(
  translations: Record<string, unknown>,
  key: string | undefined,
  params?: Record<string, unknown>
): string | null {
  if (!key) return null;
  
  // Navigate nested object: "ws.generation.started" -> translations.ws.generation.started
  const parts = key.split('.');
  let value: unknown = translations;
  
  for (const part of parts) {
    if (value && typeof value === 'object' && part in value) {
      value = (value as Record<string, unknown>)[part];
    } else {
      return null; // Key not found
    }
  }
  
  if (typeof value !== 'string') return null;
  
  // Replace {{param}} with actual values
  let result = value;
  if (params) {
    for (const [paramKey, paramValue] of Object.entries(params)) {
      result = result.replace(new RegExp(`\\{\\{${paramKey}\\}\\}`, 'g'), String(paramValue));
    }
  }
  
  return result;
}

export function useGeneration() {
  const [isGeneratingLocal, setIsGeneratingLocal] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [progressStateLocal, setProgressStateLocal] = useState<GenerationProgressState>({
    currentAgent: '',
    currentStep: '',
    progress: 0,
    messages: [],
    totalSteps: 5,
    completedSteps: 0,
    platforms: []
  });
  const { currentSpace } = useAuth();
  const { t } = useLanguage();
  const { setIsGenerating: setGlobalGenerating, setIsGeneratingBatch: setGlobalGeneratingBatch, isGenerating: isGlobalGenerating, isGeneratingBatch: isGlobalGeneratingBatch, setGlobalProgressState, registerCancelGeneration } = useGenerationContext();
  const { refreshQuota } = useQuota();
  const wsRef = useRef<WebSocket | null>(null);
  const abortRef = useRef<boolean>(false);
  const wsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const httpAbortControllerRef = useRef<AbortController | null>(null);
  // Optionnel : hors du provider (tests, usages isolés) il n'y a rien à recharger.
  // Lu via ref pour ne pas figer une version périmée dans le useCallback du batch.
  const postsContext = useContext(PostsContext);
  const refreshPostsRef = useRef(postsContext?.refreshPosts);
  useEffect(() => {
    refreshPostsRef.current = postsContext?.refreshPosts;
  });

  // Après un batch en échec ou partiel, le backend a pu persister des posts après
  // l'abandon du front (timeout, event WS perdu, annulation) : on recharge la liste
  // tout de suite puis UNE fois après un délai. Deux appels fixes, pas de boucle.
  const scheduleLatePostsRefresh = useCallback(() => {
    const refresh = () => {
      refreshPostsRef.current?.().catch((err: unknown) => {
        Logger.debug('[useGeneration] Late posts refresh failed', {
          error: err instanceof Error ? err.message : 'unknown',
        });
      });
    };
    refresh();
    setTimeout(refresh, GENERATION_LATE_POSTS_REFRESH_DELAY_MS);
  }, []);

  // Sync local state with global context
  const isGenerating = isGeneratingLocal || isGlobalGenerating;

  // progressState wrapper: keeps local + global context in sync
  const progressState = progressStateLocal;

  const setProgressState = useCallback((updater: GenerationProgressState | ((prev: GenerationProgressState) => GenerationProgressState)) => {
    setProgressStateLocal(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      setGlobalProgressState(next);
      return next;
    });
  }, [setGlobalProgressState]);

  const setIsGenerating = useCallback((value: boolean) => {
    setIsGeneratingLocal(value);
    setGlobalGenerating(value);
  }, [setGlobalGenerating]);

  const resetProgress = useCallback(() => {
    const initial: GenerationProgressState = {
      currentAgent: '',
      currentStep: '',
      progress: 0,
      messages: [],
      totalSteps: 5,
      completedSteps: 0,
      platforms: [],
      currentPlatform: undefined
    };
    setProgressStateLocal(initial);
    setGlobalProgressState(null);
  }, [setGlobalProgressState]);

  const initializePlatforms = useCallback((configs: GenerationConfig[]) => {
    const platforms: PlatformProgress[] = configs
      .filter(c => c.selected)
      .map(c => ({
        platform: c.platform || 'linkedin',
        channelId: c.id,
        status: 'pending' as PlatformStatus,
        postsGenerated: 0,
        postsRequested: c.postsCount
      }));
    setProgressState(prev => ({ ...prev, platforms }));
  }, []);

  const updatePlatformStatus = useCallback((channelId: string, status: PlatformStatus, postsGenerated?: number) => {
    setProgressState(prev => ({
      ...prev,
      currentPlatform: prev.platforms.find(p => p.channelId === channelId)?.platform,
      platforms: prev.platforms.map(p => 
        p.channelId === channelId 
          ? { ...p, status, postsGenerated: postsGenerated ?? p.postsGenerated }
          : p
      )
    }));
  }, []);

  const addProgressMessage = useCallback((agent: string, status: AgentProgress['status'], message: string, details?: string) => {
    setProgressState(prev => ({
      ...prev,
      currentAgent: agent,
      currentStep: message,
      messages: [...prev.messages, {
        agent,
        status,
        message,
        timestamp: new Date(),
        details
      }]
    }));
  }, []);

  const generatePostsWithWebSocket = useCallback(async (
    configs: GenerationConfig[],
    settings?: GenerationSettings,
    onProgress?: (progress: GenerationProgressState) => void
  ): Promise<Post[]> => {
    const selectedChannels = configs.filter(c => c.selected);
    
    if (selectedChannels.length === 0) {
      toastService.warning(t.toasts.noChannelSelected);
      return [];
    }

    if (!currentSpace?.id) {
      toastService.error(t.toasts.noSpaceSelected);
      return [];
    }

    setIsGenerating(true);
    setGlobalGeneratingBatch(true); // Marque la génération groupée comme active
    setGenerationError(null);
    resetProgress();
    initializePlatforms(selectedChannels);
    abortRef.current = false;

    const spaceId = currentSpace.id;
    const allPosts: Post[] = [];
    // V4-UX-ERROR-RECOVER : capture si AU MOINS un canal a échoué pour cause de
    // timeout/504 — utilisé pour proposer un bouton "Réessayer" en fin de batch.
    let hadTimeout = false;
    // V4-GEN-WS-EVENT-LOST-RECOVERY : si la génération a réussi côté backend mais
    // que l'event temps-réel a été perdu ET qu'on récupère une ERREUR persistée
    // (quota, échec LLM…), on remonte ce message exact plutôt que "Aucun post généré".
    let backendErrorMessage: string | null = null;
    // Au moins un canal a levé une erreur (WS error, backend error, HTTP fallback KO).
    let hadChannelFailure = false;

    for (const config of selectedChannels) {
      if (abortRef.current) break;

      // Mark current platform as generating
      updatePlatformStatus(config.id, 'generating');

      const sessionId = `gen_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      // Le backend a émis `complete` pour cette session : son résultat est
      // DÉFINITIF, même à 0 post (re-générer dupliquerait le travail backend).
      let wsCompleted = false;
      
      try {
        Logger.info('[useGeneration] Generation started for channel', {
          channelId: config.id,
          platform: config.platform || 'linkedin',
          postsCount: config.postsCount,
          spaceId,
        });
        const posts = await new Promise<Post[]>((resolve, reject) => {
          const token = authService.getToken();
          
          if (!token) {
            Logger.error('[useGeneration] No valid auth token found before WebSocket generation');
            reject(new Error('Authentication token not found or expired. Please login again.'));
            return;
          }
          
          // SEC-WS-TOKEN-URL 2026-07-06 — le token n'est PLUS en query param (fuite
          // logs serveur / historique navigateur / proxies). Il est envoyé dans le 1er
          // message WS `{type:'auth', token}` (cf. onopen), attendu par ws_server.
          const wsUrl = `${WS_URL}/ws/generation/${sessionId}`;
          Logger.debug('[useGeneration] Connecting to generation WebSocket', {
            sessionId,
            channelId: config.id,
          });

          const ws = new WebSocket(wsUrl);
          wsRef.current = ws;
          const collectedPosts: Post[] = [];

          // V4-FRONT-WS-GEN-TIMEOUT-HARDCAP 2026-05-29 — vrai timeout d'INACTIVITÉ
          // (pas un plafond dur). Ré-armé à chaque message reçu : ne ferme la connexion
          // que si le backend reste silencieux 5 min d'affilée. Avant ce fix, le timer
          // était posé une seule fois → les batchs multi-posts (>5 min total mais avec
          // progression continue) étaient tués à 5 min alors que le backend réussissait.
          // Le plafond absolu reste assuré par `generationTimeout` (10 min) plus bas.
          const armWsInactivityTimeout = () => {
            if (wsTimeoutRef.current) clearTimeout(wsTimeoutRef.current);
            wsTimeoutRef.current = setTimeout(() => {
              Logger.warn('[useGeneration] WebSocket inactivity timeout (5min sans message) — closing connection');
              ws.close();
              reject(new Error('Generation timeout'));
            }, WS_INACTIVITY_TIMEOUT_MS);
          };
          armWsInactivityTimeout();

          ws.onopen = () => {
            // SEC-WS-TOKEN-URL — auth par 1er message (le ws_server ferme 4001 sinon).
            ws.send(JSON.stringify({ type: 'auth', token }));
            addProgressMessage('connecting', 'running', t.ws.generation.connecting);

            // Calculate days_back from selectedDate if available
            const daysBack = config.selectedDate
              ? Math.ceil((Date.now() - new Date(config.selectedDate).getTime()) / (1000 * 60 * 60 * 24))
              : config.daysBack || 30;

            // Trigger generation via HTTP POST to the API.
            // The ws_server only relays Redis messages — it does not handle 'start'.
            // The API runs generation in background and publishes progress to Redis.
            // Utilise apiClient (axios) au lieu de fetch pour bénéficier de l'intercepteur 401.
            apiClient.post('/generation/start', {
              session_id: sessionId,
              space_id: spaceId,
              channel_id: config.id,
              platform: config.platform || 'linkedin',
              count: config.postsCount,
              language: settings?.language || 'fr',
              use_sources: settings?.useSources !== false,
              platform_positioning: config.positioning || undefined,
              days_back: daysBack,
              date_from: config.selectedDate ? new Date(config.selectedDate).toISOString() : undefined,
              image_provider: config.imageProvider || undefined,
              // Orientation forcée (globale au batch — identique pour chaque canal)
              forced_topic: settings?.forcedTopic?.trim() || undefined,
              forced_url: settings?.forcedUrl?.trim() || undefined,
            }).catch((err: Error) => {
              Logger.error('[useGeneration] Failed to start generation via HTTP', { sessionId, error: err?.message });
              reject(new Error('Failed to start generation'));
            });
          };

          ws.onmessage = (event) => {
            try {
              // V4-FRONT-WS-GEN-TIMEOUT-HARDCAP — ré-arme le timeout d'inactivité :
              // tant que le backend pousse des messages (progress), on ne timeout pas.
              armWsInactivityTimeout();
              const data = JSON.parse(event.data);
              Logger.debug('[useGeneration] WebSocket message received', {
                sessionId,
                channelId: config.id,
                type: data.type,
              });
              
              if (data.type === 'progress') {
                const progress = data.data;
                const agentName = progress.current_agent || progress.agent || 'unknown';
                // Try to translate i18n_key, fallback to message
                const i18nKey = progress.i18n_key;
                const i18nParams = progress.i18n_params || {};
                const translatedMessage = translateI18nKey(t, i18nKey, i18nParams);
                // V4-UX-WS-I18N-FALLBACK 2026-07-02 — clé backend inconnue + pas de
                // message : libellé générique lisible au lieu d'une étape vide.
                const message = translatedMessage || progress.message || (i18nKey ? t.ws.fallbackStep : '');
                const status = progress.status || 'running';
                // Backend sends progress_percent, fallback to progress for compatibility
                const progressPercent = progress.progress_percent ?? progress.progress ?? 0;
                const phase = progress.phase || 'init';
                
                // Backend sends all messages in each update - parse them directly
                const backendMessages = (progress.messages || []).map((m: WsAgentMessage) => {
                  const msgI18nKey = m.i18n_key;
                  const msgI18nParams = m.i18n_params || {};
                  const msgTranslated = translateI18nKey(t, msgI18nKey, msgI18nParams);
                  return {
                    agent: m.role || m.agent || 'unknown',
                    status: m.status || (progressPercent >= 100 ? 'completed' : 'running'),
                    message: msgTranslated || m.content || m.message || (msgI18nKey ? t.ws.fallbackStep : ''),
                    timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
                    details: m.details || m.metadata?.details,
                    i18nKey: msgI18nKey,
                    i18nParams: msgI18nParams
                  };
                });
                
                setProgressState(prev => {
                  const newState = {
                    ...prev,
                    currentAgent: agentName,
                    currentStep: message,
                    progress: progressPercent,
                    completedSteps: Math.floor(progressPercent / 20),
                    phase: phase,
                    // Replace messages with backend messages instead of accumulating
                    messages: backendMessages
                  };
                  onProgress?.(newState);
                  return newState;
                });
              } else if (data.type === 'complete') {
                wsCompleted = true;
                const posts = data.data?.posts || [];
                const completionI18nKey = data.data?.i18n_key;
                const completionI18nParams = data.data?.i18n_params || { count: posts.length };
                const completionMessage = translateI18nKey(t, completionI18nKey, completionI18nParams) 
                  || t.toasts.postsGenerated.replace('{count}', String(posts.length));
                
                const processedPosts = posts.map((p: WsPost) => ({
                  ...p,
                  id: p.id || `gen_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                  channelId: p.channel_id || p.channelId,
                  spaceId: p.space_id || p.spaceId,
                  network: p.network || p.platform || config.platform || 'linkedin',
                  generatedDate: new Date(p.generated_date || p.generatedDate || Date.now()),
                  created_at: new Date(p.created_at || Date.now()),
                  scheduledDate: p.scheduled_date ? new Date(p.scheduled_date) : undefined,
                  publishedDate: p.published_date ? new Date(p.published_date) : undefined,
                  sourceInspiration: p.source_inspiration,
                  generationAngle: p.generation_angle || p.angle,
                  hashtags: p.hashtags || [],
                  platform: p.platform,
                  status: p.status || 'draft',
                  // Source traceability - map backend format to frontend
                  sourceIds: p.source_ids || [],
                  sourceUrls: p.source_urls || [],
                  sourceReferences: (p.source_references || p.sources || []).map((ref: WsSourceReference) => ({
                    id: ref.source_id || ref.id || '',
                    title: ref.title || '',
                    url: ref.url || '',
                    type: ref.source_type || ref.type || 'unknown',
                    relevance: ref.relevance_score || ref.relevance || 0,
                    facts: ref.key_facts || ref.facts || [],
                    aiSummary: ref.ai_summary || '',
                    postAngles: ref.post_angles || [],
                    keywords: ref.keywords || [],
                    entityNames: ref.entity_names || [],
                  })),
                  // Quality scores from compliance agent
                  complianceScores: p.compliance_scores || undefined,
                  // V4-KNOWLEDGE-FALLBACK-UX 2026-05-29 — knowledge source quality signal
                  knowledgeSourceQuality: p.knowledge_source_quality as Post['knowledgeSourceQuality'],
                }));
                collectedPosts.push(...processedPosts);
                
                // Mark platform as completed
                updatePlatformStatus(config.id, 'completed', processedPosts.length);
                Logger.info('[useGeneration] Generation completed for channel', {
                  channelId: config.id,
                  platform: config.platform || 'linkedin',
                  postsGenerated: processedPosts.length,
                  sessionId,
                });
                
                addProgressMessage('system', 'completed', completionMessage);
                setProgressState(prev => ({ ...prev, progress: 100 }));
                
                ws.close();
                resolve(collectedPosts);
              } else if (data.type === 'error') {
                const errorI18nKey = data.data?.i18n_key;
                const errorI18nParams = data.data?.i18n_params || { error: data.data?.error || t.toasts.error };
                const errorMessage = translateI18nKey(t, errorI18nKey, errorI18nParams)
                  || data.data?.error || t.toasts.error;
                addProgressMessage('system', 'error', errorMessage);
                // Mark platform as error
                updatePlatformStatus(config.id, 'error');
                Logger.warn('[useGeneration] Generation returned backend error', {
                  channelId: config.id,
                  sessionId,
                  error: errorMessage,
                });
                reject(new Error(errorMessage));
              }
            } catch (e) {
              Logger.error('[useGeneration] Error parsing WebSocket message', {
                channelId: config.id,
                sessionId,
                error: e instanceof Error ? e.message : 'unknown_error',
              });
            }
          };

          ws.onerror = (error) => {
            if (wsTimeoutRef.current) { clearTimeout(wsTimeoutRef.current); wsTimeoutRef.current = null; }
            Logger.error('[useGeneration] WebSocket error', {
              channelId: config.id,
              sessionId,
              error,
            });
            addProgressMessage('system', 'error', t.ws.generation.failed.replace('{{error}}', t.toasts.error));
            updatePlatformStatus(config.id, 'error');
            reject(new Error('WebSocket connection failed'));
          };

          // V4-SETTINGS-LOCKEDSECTION-TS-LINT — déclaré avant ws.onclose qui le référence
          // (no-use-before-define). Timeout after 10 minutes (generation with diversity retries can take time).
          const generationTimeout = setTimeout(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.close();
              reject(new Error('Generation timeout'));
            }
          }, WS_GENERATION_HARDCAP_MS);

          ws.onclose = (event) => {
            if (wsTimeoutRef.current) { clearTimeout(wsTimeoutRef.current); wsTimeoutRef.current = null; }
            clearTimeout(generationTimeout);
            if (!event.wasClean && collectedPosts.length === 0) {
              // Fallback to HTTP if WebSocket fails
              Logger.warn('[useGeneration] WebSocket closed unexpectedly, falling back to HTTP', {
                channelId: config.id,
                sessionId,
                code: event.code,
                reason: event.reason,
              });
            }
            // Always resolve to unblock the await — safe to call multiple times (noop after first)
            resolve(collectedPosts);
          };
        });

        allPosts.push(...posts);

        if (posts.length === 0 && wsCompleted) {
          Logger.warn('[useGeneration] Backend completed with 0 posts — final, no HTTP re-generation', {
            channelId: config.id,
            sessionId,
          });
        }

        // Le WS n'a rien remonté (fermeture prématurée / event temps-réel perdu).
        // Pas de récupération/re-génération si le backend a déjà conclu (`complete`)
        // ni si l'utilisateur a annulé.
        if (posts.length === 0 && !wsCompleted && !abortRef.current) {
          // Mapper commun (posts bruts snake_case → Post front). Réutilisé par la
          // RÉCUPÉRATION et la re-génération HTTP ci-dessous pour éviter la duplication.
          const mapRawPosts = (raw: unknown[]): Post[] =>
            (raw as WsPost[]).map((p: WsPost) => ({
              ...p,
              id: p.id || `gen_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              content: p.content || '',
              status: p.status as PostStatus | undefined,
              platform: p.platform as ChannelType | undefined,
              channelId: p.channel_id || p.channelId,
              spaceId: p.space_id || p.spaceId,
              generatedDate: new Date(p.generated_date || p.generatedDate || Date.now()),
              created_at: new Date(p.created_at || Date.now()),
              scheduledDate: p.scheduled_date ? new Date(p.scheduled_date) : undefined,
              publishedDate: p.published_date ? new Date(p.published_date) : undefined,
              sourceInspiration: p.source_inspiration,
              generationAngle: p.generation_angle || p.angle,
              hashtags: p.hashtags || [],
              // Source traceability
              sourceIds: p.source_ids || [],
              sourceUrls: p.source_urls || [],
              // V4-FRONT-ANY-USAGE 2026-05-28 — typed via WsSourceReference (shared with WS path)
              sourceReferences: (p.source_references || p.sources || []).map((ref: WsSourceReference) => ({
                id: ref.source_id || ref.id || '',
                title: ref.title || '',
                url: ref.url || '',
                type: ref.source_type || ref.type || 'unknown',
                relevance: ref.relevance_score || ref.relevance || 0,
                facts: ref.key_facts || ref.facts || [],
                aiSummary: ref.ai_summary || '',
                postAngles: ref.post_angles || [],
                keywords: ref.keywords || [],
                entityNames: ref.entity_names || [],
              })),
              // V4-KNOWLEDGE-FALLBACK-UX 2026-05-29 — knowledge source quality signal
              knowledgeSourceQuality: p.knowledge_source_quality as Post['knowledgeSourceQuality'],
            }));

          // ÉTAPE 1 — RÉCUPÉRATION (V4-GEN-WS-EVENT-LOST-RECOVERY) : le backend
          // persiste le résultat terminal (Redis TTL) car le pub/sub n'a pas de
          // replay. Avant de RE-générer (coûteux : quota + ~2 min), on tente de
          // récupérer le résultat déjà produit pour CETTE session.
          let recovered = false;
          try {
            const snapshot = await generationService.getGenerationResult(sessionId);
            if (snapshot?.type === 'complete' && Array.isArray(snapshot.data.posts)) {
              // Un snapshot `complete` est définitif, même à 0 post : pas de re-génération.
              const recoveredPosts = mapRawPosts(snapshot.data.posts);
              allPosts.push(...recoveredPosts);
              recovered = true;
              Logger.info('[useGeneration] Recovered generation result via HTTP snapshot (WS event lost)', {
                channelId: config.id,
                sessionId,
                recoveredPosts: recoveredPosts.length,
              });
            } else if (snapshot?.type === 'error') {
              // Vraie erreur backend (quota, échec LLM…) : re-générer serait inutile.
              // On mémorise le message EXACT pour l'afficher au lieu du générique.
              backendErrorMessage =
                translateI18nKey(t, snapshot.data.i18n_key, snapshot.data.i18n_params)
                || snapshot.data.error
                || null;
              recovered = true; // stoppe la re-génération (l'échec est réel, pas un event perdu)
              Logger.warn('[useGeneration] Recovered backend error snapshot (no re-generation)', {
                channelId: config.id,
                sessionId,
                error: backendErrorMessage,
              });
            }
          } catch (recoveryErr) {
            Logger.debug('[useGeneration] Recovery snapshot unavailable, will fall back to HTTP re-generation', {
              sessionId,
              error: recoveryErr instanceof Error ? recoveryErr.message : 'unknown',
            });
          }

          // ÉTAPE 2 — RE-GÉNÉRATION HTTP (seulement si rien à récupérer) : ancien
          // fallback quand la génération n'a jamais abouti (aucun snapshot persisté).
          if (!recovered) {
            Logger.warn('[useGeneration] No recoverable result — using HTTP re-generation fallback', {
              channelId: config.id,
              platform: config.platform || 'linkedin',
            });
            addProgressMessage('system', 'running', t.ws.fallback.started);

            // Filter out 'newsletter' as it's not a social platform for generation
            const socialPlatform = config.platform && config.platform !== 'newsletter'
              ? config.platform as 'linkedin' | 'twitter' | 'facebook' | 'instagram'
              : 'linkedin';

            const options: GenerationOptions = {
              count: config.postsCount,
              channelId: config.id,
              platform: socialPlatform,
              language: settings?.language || 'fr',
              useSources: settings?.useSources !== false,
              forceRefreshSources: settings?.forceRefreshSources || false
            };

            // AbortController pour annuler si le composant est démonté
            const abortController = new AbortController();
            httpAbortControllerRef.current = abortController;

            const generatedPosts = await generationService.generatePosts(spaceId, {
              ...options,
              signal: abortController.signal,
            });
            if (generatedPosts && Array.isArray(generatedPosts)) {
              const processedPosts = mapRawPosts(generatedPosts);
              allPosts.push(...processedPosts);
              Logger.info('[useGeneration] HTTP fallback generation completed', {
                channelId: config.id,
                generatedPosts: processedPosts.length,
              });
            }
          }
        }
      } catch (e) {
        Logger.error('[useGeneration] Failed to generate posts for channel', {
          channelId: config.id,
          error: e instanceof Error ? e.message : 'unknown_error',
        });
        if (isTimeoutLikeError(e)) hadTimeout = true;
        hadChannelFailure = true;
        // Continue with next channel
      }
    }

    setIsGenerating(false);
    setGlobalGeneratingBatch(false); // Fin de la génération groupée

    // Rafraîchir le quota après la génération (délai pour laisser le backend indexer l'usage dans ES)
    setTimeout(() => {
      refreshQuota().catch((err) => {
        Logger.debug('[useGeneration] Failed to refresh quota after generation', { error: err?.message });
      });
    }, 2000);

    // Somme des demandés sur tous les canaux sélectionnés (multi-canal).
    const requestedTotal = selectedChannels.reduce(
      (sum, c) => sum + (c.postsCount ?? 0),
      0,
    );
    if (hadChannelFailure || allPosts.length < requestedTotal) {
      scheduleLatePostsRefresh();
    }

    if (allPosts.length === 0) {
      // V4-GEN-WS-EVENT-LOST-RECOVERY : un message backend récupéré (quota, échec
      // LLM…) est PRIORITAIRE sur le générique "Aucun post généré" — l'utilisateur
      // doit savoir POURQUOI, pas soupçonner sa connexion.
      const finalErrorMessage = backendErrorMessage || t.toasts.noPostGenerated;
      setGenerationError(finalErrorMessage);
      // V4-UX-ERROR-RECOVER : si la génération a expiré (504/timeout), on remplace
      // le message générique par un toast actionnable avec bouton "Réessayer".
      if (hadTimeout) {
        toastService.error(
          t.toasts.generationTimeout ?? 'La génération a expiré',
          t.toasts.generationTimeoutDesc ?? 'Le service est lent en ce moment. Vous pouvez réessayer.',
          {
            label: t.toasts.retry ?? 'Réessayer',
            onClick: () => { void generatePostsWithWebSocket(configs, settings, onProgress); },
          },
        );
      } else {
        toastService.error(finalErrorMessage);
      }
      return [];
    }

    // V4-FRONT-GEN-COUNT-MISMATCH-TOAST : si la génération livre MOINS de posts
    // que demandé (filet backend "jamais 0 post", ou échec diversité sur un
    // canal), on prévient explicitement au lieu d'un toast succès qui masque
    // l'écart (l'utilisateur demande 2, en reçoit 1, sans savoir pourquoi).
    if (requestedTotal > 0 && allPosts.length < requestedTotal) {
      toastService.warning(
        (t.toasts.partialGeneration ??
          '{delivered} post(s) généré(s) sur {requested} demandé(s) — contenu trop similaire, réessayez avec un autre sujet.')
          .replace('{delivered}', String(allPosts.length))
          .replace('{requested}', String(requestedTotal)),
      );
      return allPosts;
    }

    toastService.success(
      t.toasts.postsGenerated.replace('{count}', String(allPosts.length)),
      t.toasts.postsGeneratedFeedbackHint,
    );
    return allPosts;
  }, [currentSpace, t, addProgressMessage, resetProgress, refreshQuota, scheduleLatePostsRefresh]);

  const cancelGeneration = useCallback(() => {
    abortRef.current = true;
    Logger.warn('[useGeneration] Generation cancelled by user');
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }
    // Annuler le fallback HTTP si actif
    if (httpAbortControllerRef.current) {
      httpAbortControllerRef.current.abort();
      httpAbortControllerRef.current = null;
    }
    setIsGenerating(false);
    setGlobalGeneratingBatch(false); // Annulation = fin de la génération groupée
    addProgressMessage('system', 'error', t.toasts.generationCancelled);
    toastService.warning(t.toasts.generationCancelled);
  }, [addProgressMessage, setGlobalGeneratingBatch]);

  // Register cancelGeneration in context so GlobalProgressOverlay can call it
  // even if MobileGeneratePanel is unmounted
  useEffect(() => {
    registerCancelGeneration(cancelGeneration);
    return () => {
      registerCancelGeneration(null);
    };
  }, [cancelGeneration, registerCancelGeneration]);

  const improvePost = async (content: string, instructions?: string, platform?: string): Promise<{ content: string; explanation?: string } | null> => {
    setIsGenerating(true);
    setGenerationError(null);
    const loadingToast = toastService.loading(t.toasts.improving);

    try {
      const result = await generationService.improvePost(content, instructions, platform);
      Logger.info('[useGeneration] Post improvement succeeded', { platform: platform ?? null });
      toastService.dismiss(loadingToast);
      toastService.success(t.toasts.postImproved);
      // Rafraîchir le quota après amélioration de post (consomme des crédits)
      setTimeout(() => { refreshQuota().catch(() => {}); }, 2000);
      return result;
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : 'unknown_error';
      Logger.error('[useGeneration] Post improvement failed', {
        platform: platform ?? null,
        error: errMsg,
      });
      setGenerationError(t.toasts.improveError.replace('{message}', errMsg));
      toastService.dismiss(loadingToast);
      // V4-UX-ERROR-RECOVER : sur timeout/504, proposer un bouton "Réessayer"
      // pour relancer l'amélioration sans recharger la page.
      if (isTimeoutLikeError(err)) {
        toastService.error(
          t.toasts.improveTimeout ?? 'L\'amélioration a expiré',
          t.toasts.improveTimeoutDesc ?? 'Le service est lent en ce moment. Vous pouvez réessayer.',
          {
            label: t.toasts.retry ?? 'Réessayer',
            onClick: () => { void improvePost(content, instructions, platform); },
          },
        );
      } else {
        toastService.error(t.toasts.improveError.replace('{message}', errMsg));
      }
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  const syncSources = async (): Promise<boolean> => {
    if (!currentSpace?.id) {
      toastService.error(t.toasts.noSpaceSelected);
      return false;
    }
    
    setIsSyncing(true);
    const loadingToast = toastService.loading(t.toasts.syncing);
    
    try {
      const result = await generationService.syncSpace(currentSpace.id);
      Logger.info('[useGeneration] Source sync completed', {
        spaceId: currentSpace.id,
        success: result.success,
        synced: result.synced,
      });
      toastService.dismiss(loadingToast);
      
      if (result.success) {
        toastService.success(t.toasts.syncSuccess.replace('{count}', String(result.synced)));
      } else {
        toastService.warning(result.message);
      }
      return result.success;
    } catch (err: unknown) {
      Logger.error('[useGeneration] Source sync failed', {
        spaceId: currentSpace.id,
        error: err instanceof Error ? err.message : 'unknown_error',
      });
      toastService.dismiss(loadingToast);
      toastService.error(t.toasts.syncError);
      return false;
    } finally {
      setIsSyncing(false);
    }
  };

  // Cleanup on unmount : annuler toute requête HTTP fallback en cours
  useEffect(() => {
    return () => {
      httpAbortControllerRef.current?.abort();
    };
  }, []);

  return {
    generatePosts: generatePostsWithWebSocket,
    improvePost,
    syncSources,
    cancelGeneration,
    resetProgress,
    isGenerating,
    isSyncing,
    generationError,
    progressState
  };
}
